/**
 * The onboarding step model, shared by the modal and the catalog banner.
 *
 * Both surfaces have to agree on what "done" means, or a user clears a step in
 * one and the other keeps demanding it. So completion is derived from REAL
 * account state — a saved search exists, Telegram is connected, an ИНН is set —
 * and never from a "step 2 finished" flag. A flag can disagree with reality
 * (the user deletes their only saved search); the state cannot.
 */

import { countryConfig } from '../country';

export type OnboardingStepId = 'search' | 'email' | 'telegram' | 'inn';

/**
 * Is the company step answered? An id on file — or, on Spain
 * (`companyIdForm.keepsTypedName`), a company name saved as typed when the
 * register had no match (owner, 2026-10-21: «вбивает название — просто
 * сохраняем»). Exactly `Boolean(user.tin)` on UZ/KZ. One rule for the modal,
 * the catalogue strip and the dashboard checklist.
 */
export function companyStepDone(
  user: { tin?: string | null; typedCompanyName?: string | null } | null | undefined,
): boolean {
  if (user?.tin) return true;
  return Boolean(countryConfig.companyIdForm.keepsTypedName && user?.typedCompanyName);
}

/**
 * Has this account pressed «Omitir» on the company step, and does this deployment
 * keep that (`features.companySkipRemembered`, Spain — persona test 22.10, D6)?
 * Then the sheet, the catalogue strip and the dashboard checklist treat the step as
 * retired, exactly as they do for the device's «no company» answer (`innSkipped`).
 * Always false on UZ/KZ.
 */
export function companySkipRemembered(
  user: { companyStepSkipped?: boolean } | null | undefined,
): boolean {
  return countryConfig.features.companySkipRemembered === true && user?.companyStepSkipped === true;
}

export interface OnboardingState {
  hasSavedSearch: boolean;
  telegramConnected: boolean;
  hasTin: boolean;
  /** The user said they have no ИНН. See `innSkipped` below for why this exists. */
  innSkipped: boolean;
  /**
   * E-mail confirmation, for the surfaces that ask for it.
   *
   * ⚠️ OPTIONAL, and that is the whole mechanism: a caller that does not pass
   * it gets no e-mail step at all. Only the catalog strip asks; the onboarding
   * MODAL deliberately does not (2026-09-21, owner — the modal is the 60-second
   * "get your first alert" path, and a code screen in the middle of it is a
   * dead end for anyone whose mail is on another device), and neither does the
   * dashboard checklist. Both simply omit the field.
   *
   * `true` / `false` mean "this account has a real address and we know its
   * state". `undefined` / `null` mean there is nothing to ask — a
   * Telegram-native account whose synthetic `telegram_<id>@users.uztender.com`
   * address can never receive a code, or a `/auth/me` payload that has not told
   * us yet. Treating "unknown" as "unverified" would put a permanent,
   * unfinishable row in front of both.
   */
  emailVerified?: boolean | null;
}

/**
 * Which steps still need doing, in the order they should be asked.
 *
 * Order is deliberate. The saved search comes first because it is the only step
 * that delivers something on its own, and asking for a government identifier
 * before the product has demonstrated value is how you get `111111111`.
 * Telegram goes last — see the note at its push below.
 */
export function pendingSteps(s: OnboardingState): OnboardingStepId[] {
  return orderedSteps(s)
    .filter((step) => !step.done)
    .map((step) => step.id);
}

export interface OnboardingStep {
  id: OnboardingStepId;
  done: boolean;
}

/**
 * Every step that still APPLIES to this user, done ones included, in ask order.
 *
 * `pendingSteps` alone cannot drive a progress display: a surface that only
 * receives what is outstanding has to reconstruct the rest, and both surfaces
 * did it the same wrong way — `done = 3 - pending.length`. That counts a
 * RETIRED ИНН step as an accomplishment, so the moment a user without a company
 * pressed «Позже» the strip read «1 из 3» for work they had not done.
 *
 * Dropping a retired step from the list fixes the numerator and the denominator
 * together: it becomes «0 из 2», which is both true and finishable. That is the
 * whole reason this returns a list instead of a count — a count cannot express
 * "this step no longer exists for you".
 */
export function orderedSteps(s: OnboardingState): OnboardingStep[] {
  const out: OnboardingStep[] = [];
  out.push({ id: 'search', done: s.hasSavedSearch });
  // Second, and only where it was asked for (see `emailVerified`). It belongs
  // above the ИНН because it is the cheapest step on the list — one code — and
  // below the saved search because the saved search is the one that pays out.
  // A VERIFIED address stays in the list as a DONE row rather than vanishing:
  // the strip shows done rows on purpose, so «3 из 4» can be read.
  if (typeof s.emailVerified === 'boolean') out.push({ id: 'email', done: s.emailVerified });
  // ⚠️ `innSkipped` is what keeps the persistent banner from becoming a trap.
  //
  // The banner cannot be closed — it clears by being COMPLETED. That is fine for
  // a search and for Telegram, which any user can do. It is not fine for an ИНН:
  // plenty of legitimate users have no company at all (individuals researching
  // the market, employees of a company registered to someone else), and for them
  // an uncloseable "add your ИНН" would nag forever with no possible resolution.
  //
  // So "у меня нет ИНН" is a real answer that permanently retires the step. It
  // costs one localStorage flag and it is the difference between a reminder and
  // a dark pattern.
  // Retired steps leave the list entirely — see `orderedSteps` for why that has
  // to be an omission rather than a permanently-unticked row.
  if (s.hasTin || !s.innSkipped) out.push({ id: 'inn', done: s.hasTin });
  // Telegram LAST. It is the only step that leaves the site — it needs an
  // external app, and its widget is the one that can fail outright (an
  // unregistered bot domain renders "Bot domain invalid" in place of the
  // button). Anything placed after it inherits that failure and never gets
  // asked, so the steps that always work go first.
  //
  // Omitted entirely on a telegram-off deployment (messaging.telegram =
  // false; KZ was one until 2026-10-14): there is no bot, so a telegram step
  // would be a permanently-unfinishable row (`telegramConnected` can never
  // become true) — the counter would never reach «done». That is why it must
  // be dropped from the list, not shown as a forever-unticked step.
  if (countryConfig.messaging.telegram) {
    out.push({ id: 'telegram', done: s.telegramConnected });
  }
  return out;
}

/** Nothing left to ask. Both surfaces hide themselves entirely. */
export function isOnboardingComplete(s: OnboardingState): boolean {
  return pendingSteps(s).length === 0;
}
